import { ArrowBack } from "@mui/icons-material"
import { Box, CircularProgress, IconButton, makeStyles } from "@material-ui/core"
import {
  themePalette,
  webkitScrollbar,
  webkitscrollbarTrack,
} from "./styles"
import {
  CloudAccessSection,
  DangerZoneSection,
  NameSection,
  StatusSection,
  SummarySection,
} from "./RightPanelSections"

const useStyles = makeStyles((theme) => ({
  primaryButton: {
    backgroundColor: theme.palette.surface.accentSoft,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.surface.accentBorder}`,
    textTransform: "uppercase",
    boxShadow: "none",
    transition: theme.transitions.create(["background-color", "border-color", "box-shadow", "color"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      backgroundColor: theme.palette.surface.selected,
      borderColor: theme.palette.primary.dark,
      color: theme.palette.primary.dark,
      boxShadow: "none",
    },
  },
  saveNameButton: {
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: `1px solid ${theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
      color: theme.palette.primary.contrastText,
      boxShadow: "none",
    },
  },
  warningButton: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    border: `1px solid ${theme.palette.warning.main}`,
    textTransform: "uppercase",
    boxShadow: "none",
    transition: theme.transitions.create(["background-color", "border-color", "box-shadow", "color"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      backgroundColor: "#fde9a8",
      borderColor: theme.palette.warning.dark,
      color: theme.palette.warning.dark,
      boxShadow: "none",
    },
  },
  outlinedButton: {
    backgroundColor: theme.palette.common.white,
    borderColor: theme.palette.border.button,
    color: theme.palette.text.primary,
    textTransform: "uppercase",
    transition: theme.transitions.create(["background-color", "border-color", "box-shadow", "color"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
      boxShadow: "none",
    },
  },
  deleteDataButton: {
    backgroundColor: "rgba(148, 163, 184, 0.08)",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    boxShadow: "none",
    transition: theme.transitions.create(["background-color", "border-color", "box-shadow", "color"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.08)",
      borderColor: theme.palette.error.light,
      color: theme.palette.error.dark,
      boxShadow: "none",
    },
  },
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    border: `1px solid ${theme.palette.error.main}`,
    textTransform: "uppercase",
    boxShadow: "none",
    transition: theme.transitions.create(["background-color", "border-color", "box-shadow", "color"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
      borderColor: theme.palette.error.dark,
      color: theme.palette.error.contrastText,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(239, 68, 68, 0.14)",
      borderColor: "rgba(239, 68, 68, 0.12)",
      color: "rgba(255, 255, 255, 0.72)",
    },
  },
}))

// Panel derecho de detalle.
// Orquesta secciones visuales sin concentrar toda la UI en un solo bloque enorme.
export default function RightPanel({
  isMobile,
  mobileView,
  setMobileView,
  rightPanelLoading,
  selectedMeter,
  editableName,
  setEditableName,
  handleNameBlur,
  loadingSave,
  loadingStateChange,
  selectedHasChildren,
  handleMeterStatusChange,
  selectedCloudSource,
  selectedIsChild,
  shouldShowCloudActionButton,
  selectedCloudButtonDisabled,
  renderCloudAccessBadge,
  handleCloudAccessAction,
  openAffirmModal,
}) {
  const classes = useStyles()
  const productionUiLock = true
  const dangerZoneUiLock = false

  const summaryChipLabel = selectedMeter
    ? [
      selectedMeter.empresa,
      selectedMeter.zona,
      String(selectedMeter.jerarquia_nombre_completa || "").replace(/\s*\/\s*/g, " / "),
    ]
      .map((value) => {
        const normalizedValue = String(value || "").trim()
        return normalizedValue || "-"
      })
      .join(" / ")
    : ""

  return (
    <Box
      sx={{
        position: "relative",
        flex: isMobile ? "0 0 100%" : "0 0 50%",
        minWidth: 0,
        maxWidth: isMobile ? "100%" : "50%",
        boxSizing: "border-box",
        backgroundColor: themePalette.surface.panel,
        display: isMobile && mobileView !== "detail" ? "none" : "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {rightPanelLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: themePalette.surface.page,
            opacity: 0.82,
            backdropFilter: "blur(3px)",
          }}
        >
          <CircularProgress size={28} style={{ color: themePalette.primary.main }} />
        </Box>
      )}

      <Box sx={{ borderBottom: `1px solid ${themePalette.border.main}`, padding: isMobile ? "0.65rem 0.9rem" : "0.75rem 1rem", background: `linear-gradient(180deg, ${themePalette.common.white} 0%, ${themePalette.surface.soft} 100%)` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {isMobile && (
            <IconButton size="small" onClick={() => setMobileView("list")} sx={{ color: themePalette.primary.main }}>
              <ArrowBack fontSize="small" />
            </IconButton>
          )}
          <Box>
            <span style={{ fontSize: "0.9rem", fontWeight: 700, color: themePalette.text.disabled, textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "0.2rem", display: "inline-block" }}>
              Configuracion del dispositivo
            </span>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: isMobile ? "0.65rem 0.75rem" : "0.75rem 0.9rem",
          display: "flex",
          flexDirection: "column",
          rowGap: "0.6rem",
          "&::-webkit-scrollbar": { ...webkitScrollbar },
          "&::-webkit-scrollbar-track": { ...webkitscrollbarTrack },
          "&::-webkit-scrollbar-thumb": { background: themePalette.primary.main },
        }}
      >
        {!selectedMeter ? (
          <Box
            sx={{
              padding: "1rem",
              borderRadius: "4px",
              border: `1px solid ${themePalette.border.soft}`,
              backgroundColor: themePalette.surface.soft,
              color: themePalette.text.disabled,
            }}
          >
            Selecciona un dispositivo del arbol para ver sus datos y configuracion.
          </Box>
        ) : (
          <>
            <SummarySection
              selectedMeter={selectedMeter}
              summaryChipLabel={summaryChipLabel}
              showHierarchyChip={!selectedHasChildren}
            />

            {!selectedHasChildren && (
              <NameSection
                editableName={editableName}
                setEditableName={setEditableName}
                handleNameBlur={handleNameBlur}
                loadingSave={loadingSave}
                classes={classes}
              />
            )}

            {!selectedHasChildren && (
              <StatusSection
                selectedMeter={selectedMeter}
                handleMeterStatusChange={handleMeterStatusChange}
                loadingStateChange={loadingStateChange}
              />
            )}

            <CloudAccessSection
              isMobile={isMobile}
              selectedMeter={selectedMeter}
              selectedCloudSource={selectedCloudSource}
              selectedIsChild={selectedIsChild}
              shouldShowCloudActionButton={shouldShowCloudActionButton}
              selectedCloudButtonDisabled={selectedCloudButtonDisabled}
              renderCloudAccessBadge={renderCloudAccessBadge}
              handleCloudAccessAction={handleCloudAccessAction}
              productionUiLock={productionUiLock}
              classes={classes}
            />

            <DangerZoneSection
              isMobile={isMobile}
              openAffirmModal={openAffirmModal}
              productionUiLock={dangerZoneUiLock}
              classes={classes}
            />
          </>
        )}
      </Box>
    </Box>
  )
}
